import { MatDialogRef } from '@angular/material/dialog';
import { DateTime } from 'luxon';
import { Observable, Subscription, combineLatest, from, fromEvent, interval, of } from 'rxjs';
import { debounceTime, map, mergeMap, startWith, switchMap, take, tap } from 'rxjs/operators';
import { isAnnotationSeries } from 'src/app/model/annotation-series';
import { IConfigLoadedSeriesData } from 'src/app/model/config-loaded-series-data';
import { ITab } from 'src/app/model/tab';
import { ICard, StackCardDialogInterface } from 'src/app/model/thingie/thingie-card';
import { GraphConfigSeriesDataLoaderService } from 'src/app/services/data/series-data-loader/graph-config-series-data-loader.service';
import { PlotlyGeneratorProviderService } from 'src/app/services/plotly-generator/plotly-generator-provider.service';
import { TabStorageService } from 'src/app/services/tab-storage/tab-storage.service';
import { ThingieService } from 'src/app/services/thingie/thingie-service.service';
import { SubSink } from 'subsink';
import {
  DisplayThingie,
  IGroupedThingies
} from '../../components/list-thingies/list-thingies.component';
import {
  createDefaultTab
} from '../default-object-creator';
import { PlotDataWithIdentification } from '../input-converter/plotly-trace-generator';
import { Offsetting } from '../offsetting';
import { TabLoader } from '../tab-loader';
import { getThingieGroupId } from '../thingie-group-helper';
import { ThingieStatusHelper } from '../thingie-status-helper';
import { defaultThingieComparator } from './default-thingie-comparator';

export enum CreateStackCard {

  /**
   * Create a stack card only if there are multiple thingies in the group
   */
  Default,

  /**
   * Create a stack card even if there is only one thingie in the group
   */
  Always
}

/**
 * Create cards for a thingie group: If the group contains one thingie,
 * create a single card. If it contains multiple thingies, create
 * a stacked card plus one card per thingie.
 *
 * @param group thingie group, to be converted into cards
 * @param thingieService service to retrieve thingies
 * @param stackMode if to create a stack card for single-replicate-experiments, too
 * @returns list of cards
 */
export function cardsFromThingieGroup(
  group: IGroupedThingies,
  thingieService: ThingieService,
): ICard[] {
  // Generate a distinct card per thingie
  const cards: ICard[] = group.thingies.map(thingie => ({
    popupTab: createDefaultTab(),
    previewTab: createDefaultTab(),
    isStack: false,
    isSingle: group.thingies.length === 1,
    revision: 0,
    thingie$: thingieService.getThingieById(thingie.thingieId),
    thingieIds: [thingie.thingieId],
    thingieNames: [thingie.thingie.name],
    project: thingie.project,
    hasAnyDataPoints: false,
    hasError: false,
    isPinned: group.pinned,
    isLoading: true,
    isSingleCardRunning: checkRunningThingie(thingie),
    thingieGroupId: thingie.thingieGroupId
  }));

  // Generate a stack-card for the whole group of thingies
  const stackedCard: ICard = generateGroupCard(
    cards,
    group
  );

  return [...cards, stackedCard];
}

/**
 * Generate a card for a stack of thingie cards.
 *
 * @param baseThingie The thingie at position 0 in the group
 * @param cards The cards of the group
 * @param group The group itself
 * @returns
 */
function generateGroupCard(
  cards: ICard[],
  group: IGroupedThingies
): ICard {
  const sortedThingies = group.thingies
    .map(t => t.thingie)
    .sort(defaultThingieComparator);

  return {
    popupTab: createDefaultTab(),
    previewTab: createDefaultTab(),
    isStack: true,
    isSingle: group.thingies.length === 1,
    revision: 0,
    thingie$: of(group.thingies[0].thingie),
    thingieIds: sortedThingies.map(t => t._id!),
    thingieNames: sortedThingies.map(t => t.name),
    project: sortedThingies[0]?.project ?? '',
    hasAnyDataPoints: false,
    hasError: false,
    isPinned: false,
    isLoading: true,
    thingieGroupId: getThingieGroupId(group.thingies.map(t => t.thingieId)),
    groupRunning$: combineLatest(cards.map(card => card.thingie$))
      .pipe(
        map(
          thingies => thingies.flatMap(
            thingie => thingie.processes.flatMap(
              process => process.tasks.map(
                task => ThingieStatusHelper.getTasksStatus([task])
              )
            )
          )
        ),
        map(statusInfos => statusInfos.some(
          statusInfo => statusInfo.status !== 'Planned'
            && statusInfo.status !== 'Finished'
            && statusInfo.status !== 'No task'
          // Why 'Planned' & 'Finished' & 'No Task' ? see ThingieStatusHelper::getTasksStatus()
        )),
        startWith(false)
      ),
    groupArchived$: combineLatest(cards.map(card => card.thingie$))
      .pipe(
        map(thingies => thingies.every(thingie => thingie.archived)),
        startWith(false)
      ),
    anyArchived$: combineLatest(cards.map(card => card.thingie$))
      .pipe(
        map(thingies => thingies.some(thingie => thingie.archived)),
        startWith(false)
      ),
    canBeArchived$: combineLatest(cards.map(card => card.thingie$))
      .pipe(
        map(thingies => thingies.every(thingie => ThingieStatusHelper.canBeArchived(thingie))),
        startWith(false)
      )
  };
}

function checkRunningThingie(dt: DisplayThingie) {
  const statuses = dt.thingie.processes.flatMap(process => process.tasks.map(task =>
    ThingieStatusHelper.getTasksStatus([task])));
  return statuses.some(status => status.status === 'Running');
}

/**
 * Initialize cards with with tab data
 *
 * @param cards
 * @param tabStorage service to retrieve tab data
 * @param generator service for plotl generator
 * @param dataloader service for loading data in graph config
 */
export function initializeCards(
  cards: ICard[],
  tabStorage: TabStorageService,
  dataloader: GraphConfigSeriesDataLoaderService,
  generator: PlotlyGeneratorProviderService,
  subscriptions: SubSink
) {
  // for all cards, delay card by index * 50ms, to process cards with a small
  // delay between cards, and thereby in the correct order.
  const delayedCards = from(cards).pipe(
    mergeMap((card, idx) => interval(idx * 50).pipe(
      take(1),
      map(() => card)
    ))
  );
  // for all cards, get graph config and load data
  subscriptions.sink = delayedCards.pipe(
    mergeMap((card) => {
      let getTabStep: Observable<ITab | undefined> = of(undefined);
      if (card.previewTab.layout === undefined) {
        getTabStep = from(getTabForCard(card, tabStorage)).pipe(
          tap(tab => {
            if (tab !== undefined) {
              card.previewTab = tab;
              card.popupTab = tab;
            }
          })
        );
      }
      return getTabStep.pipe(switchMap(() => from(loadCardData(card, generator, dataloader))));
    })
  )
    .subscribe();
}

/**
 * Creates a graph config for a card.
 *
 * Gets the first tab's graph config and modifies it for the dashboard.
 *
 * If a card contains multiple thingies, take one of the first graphs and add
 * the other thingies' series as external series.
 *
 * @param card card to build the graphconfig for
 * @param tabStorage
 * @return graph config
 */
export function getTabForCard(card: ICard, tabStorage: TabStorageService): Promise<ITab | undefined> {
  return TabLoader.getTabForDashboardCard(tabStorage, card.thingie$, card.thingieGroupId, card.thingieIds, !card.isStack);
}

/**
 * Load data for the card, and update the card with it.
 *
 * @param card card to be filled with data
 * @param generator service for plotl generator
 * @param dataloader service for loading data in graph config
 */
async function loadCardData(card: ICard, generator: PlotlyGeneratorProviderService, dataloader: GraphConfigSeriesDataLoaderService) {
  const tab = card.popupTab;
  const graphConfig = tab.graphConfig;
  // generate layout
  const layoutGenerator = generator.getLayoutGenerator(graphConfig);
  await layoutGenerator.forTab(tab);
  const dashboardLayout = layoutGenerator.createDashboardLayout(tab);

  // set config
  const plotlyConfig = {
    displayModeBar: false // remove the mode bar
  };
  // load data
  const output: PlotDataWithIdentification[] = [];
  let hasData = false;
  let hasError = false;
  let calibrationWarning: string | undefined;
  const nDataPoints = 300;
  let data: IConfigLoadedSeriesData[] = [];
  try {
    data = await dataloader.getDataLoader().loadData(graphConfig, nDataPoints);
  } catch (e) {
    hasError = true;
  }
  if (graphConfig.autoOffsets.mode === 'LIVE') {
    const offsetRequest = Offsetting.requestFromGraphConfig(graphConfig);
    // data is already loaded, so use it for offsetting
    offsetRequest.data = data;
    try {
      await Offsetting.handleAutoOffsetRequestedEvent(offsetRequest, dataloader);
    } catch {
      // probably not enough data yet, so don't show anything
      card.hasAnyDataPoints = false;
      card.isLoading = false;
      return;
    }
  }
  data.forEach(series => {
    if (isAnnotationSeries(series.series)) {
      // we are not drawing annotations for the dashboard charts.
      // But due to a bug in plotly with hovering, cannot skip the series.
      // (When the skipped series is on the first axis, hovering does not check all subplots,
      // but only the hovered subplot, which is a bug.)
      // So rather generate an empty series and proceed.
      series.x = [];
      series.y = [];
    }
    if (series.x[0] !== undefined) {
      hasData = true;
    }
    if (series.series.dataItemDescription.value?.valueInfo[0]?.unit === 'yes/no') {
      // add last point at current time
      const lastY = series.y[series.y.length - 1];
      if (lastY !== undefined) {
        let now = new Date().getTime();
        if (series.seriesStopDate !== undefined) {
          now = DateTime.fromISO(series.seriesStopDate).toMillis();
        }
        series.x.push(now);
        series.y.push(lastY);
      }
    }
    if (series.subseries === 'calibrationWarning') {
      calibrationWarning = series.y[series.y.length - 1];
      return;
    }
    let plotData = generator.getTraceGenerator(graphConfig).generateTrace(series, tab);
    if (plotData.hovertemplateShort) {
      plotData.hovertemplate = plotData.hovertemplateShort;
    }
    if (plotData.type === 'scattergl') {
      // on the dashboard, don't use scattergl because it uses webgl contexts, which are a limited resource.
      // rather use d3 scatter plots here that don't require webgl contexts.
      plotData.type = 'scatter';
    }
    plotData.hovertemplate = '';
    plotData.hoverinfo = 'none';
    output.push(plotData);
  });
  card.previewTab = {
    backendId: null,
    graphConfig,
    outputData: output,
    layout: dashboardLayout,
    legend: tab.legend,
    plotlyConfig,
    _isDashboardCard: false,
    _extraData: {
      calibrationWarning
    }
  };
  card.hasAnyDataPoints = hasData;
  card.hasError = hasError;
  card.revision += 1;
  card.isLoading = false;
}

export function observeResizeEvents(
  resizeSubscription: Subscription | undefined,
  isDialogResizing: boolean,
  dialogData: StackCardDialogInterface | undefined,
  dialogRef: MatDialogRef<any, string> | undefined,
  scrollbarWidth: number | undefined,
  cardSize: { width: number; height: number } | undefined
) {
  if (resizeSubscription) {
    resizeSubscription.unsubscribe();
  }
  resizeSubscription = fromEvent(window, 'resize')
    .pipe(
      debounceTime(10)
    )
    .subscribe(() => onCardStackDialogResize(
      isDialogResizing,
      dialogData,
      dialogRef,
      scrollbarWidth,
      cardSize
    ));
}

export function onCardStackDialogResize(
  isDialogResizing: boolean,
  dialogData: StackCardDialogInterface | undefined,
  dialogRef: MatDialogRef<any, string> | undefined,
  scrollbarWidth: number | undefined,
  cardSize: { width: number; height: number } | undefined
) {
  if (isDialogResizing) {
    return;
  }
  if (dialogData?.popupMode !== 'single') {
    // unset any size constraints
    dialogRef?.updateSize();
    return;
  }
  isDialogResizing = true;
  try {
    // calc(100vw - 40px)
    const availableWidth = window.innerWidth - 40;
    // Might not work when having two browser windows open side-by-side
    const minWidth = 649;
    // 16px left and right
    const dialogPadding = 42;
    // 8px between scroll bar and cards
    const contentPaddingRight = 8;
    // flexbox's column gap
    const gap = 16;
    const maxWidth =
      Math.max(availableWidth - dialogPadding, minWidth)
      - (scrollbarWidth ?? 0 + contentPaddingRight);
    // Fallback of 300 px is the currently-used (20.03.23,
    // frontend, not Figma) card width
    const cardWidth = cardSize?.width ?? 300;
    // calculate max number of cards per row
    let spaceUsed = 0;
    const numCards = dialogData!.singleCards.length;
    for (let i = 1; i <= numCards; ++i) {
      let slotSize = cardWidth;
      if (i !== 1) {
        slotSize += gap;
      }
      if ((spaceUsed + slotSize) > maxWidth) {
        break;
      }
      spaceUsed += slotSize;
      if (i === numCards) {
        break;
      }
    }
    dialogRef?.updateSize(
      `${spaceUsed + contentPaddingRight + dialogPadding}px`,
      `${window.innerHeight - 40}px`
    );
  } finally {
    isDialogResizing = false;
  }
}
